import React from 'react';
import { TouchableOpacity } from 'react-native';
import { get, isEmpty, isNil, max } from 'lodash';

import AutoLayout from '@common/components/AutoLayout';
import ObjectRender from '@common/screens/RenderScreen/ObjectRender';
import { componentName } from '@common/constants/shared';
import { getValueBindingComponent } from '@nocode/components/shared';

// For V4, we don't need to group the children by section
const Item = ({ item, attrs, index, isActive = false }: any) => {
  const { children, rowMargin, databaseOptions, record } = attrs;
  const attrId = get(attrs, 'initId', attrs.id);
  const itemHeight: number | undefined = max(
    children.map((child: any) => Number(child.height || 0))
  );
  const itemWidth: number | undefined = max(
    children.map((child: any) => Number(child.width || 0))
  );

  let newChildren = children.map((child: any) => {
    const currentListIds = {
      [databaseOptions?.id]: item?.id,
      [attrId]: item?.id,
      ...(attrs.currentListIds || {}),
    };

    const selectedItem = {
      itemId: item?._id,
      tableId: attrs.databaseUuid,
      itemIndex: index,
      externalId: item?.id,
      externalRecord: item,
    };

    const currentRecordIds = {
      [databaseOptions?.tableId]: item?.id,
      ...(record?.id && {
        [record?._meta?.tableId]: record?.id,
      }),
    };

    const groupActionId = attrs?.actions && Object.keys(attrs.actions)[0];

    return {
      ...child,
      selectedItem,
      groupActionId,
      record: { ...item, listObjectId: attrs.id },
      isInCustomList: true,
      currentListId: attrs.id,
      parentListDatabaseUuid:
        attrs.parentListDatabaseUuid || attrs.databaseUuid,
      parentListItemId: attrs.parentListItemId || attrs.record?._id,
      ...(isNil(child.actions) || isEmpty(child.actions)
        ? { actions: attrs.actions }
        : {
            actions: {
              ...child?.actions,
              ...attrs?.actions,
            },
          }),
      allowListAction: isEmpty(child?.actions) && !isEmpty(groupActionId),
      currentListIds,
      itemIndex: index,
      currentRecordIds,
      screenUuid: attrs?.screenUuid,
    };
  });

  const updateAttributeSectionByShrink = (obj: any) => {
    if (obj.shrinkElementId) {
      const shrinkElement = obj.children.find(
        (e: { id: string }) => e.id === obj.shrinkElementId
      );
      if (shrinkElement) {
        if (shrinkElement.type === componentName.Image) {
          const getImageUrl = () => {
            const { imageType } = shrinkElement;
            switch (imageType) {
              case 'uploaded':
                return shrinkElement?.filename1x;
              case 'url':
                return getValueBindingComponent(shrinkElement, 'imageURL');
              default:
                return getValueBindingComponent(shrinkElement, 'url');
            }
          };
          const url = getImageUrl();
          const hasPlaceholder = get(
            shrinkElement,
            'imageUrl.binding.options',
            {}
          );
          const isUrl =
            !url && hasPlaceholder.placeholderImageEnabled
              ? hasPlaceholder?.placeholderImage
              : isEmpty(hasPlaceholder) && !url
              ? ''
              : url;
          if (!isUrl) {
            const newObj = JSON.parse(JSON.stringify(obj));
            newObj.height -= shrinkElement.height;
            newObj.children = newObj.children.filter(
              (e: any) => e.id !== shrinkElement.id
            );
            // update position of another obj behind shrinked obj
            sectionGroup.forEach((child: any) => {
              if (child.y > newObj.y + obj.height) {
                child.y -= shrinkElement.height;
              }
            });
            return newObj;
          }
        }
      }
    }
    return obj;
  };

  let sectionGroup: Array<any> = [];
  if (newChildren.length > 1 && newChildren[0].type == 'section') {
    let el = newChildren[0];
    let lastBottomLeft = 0;
    for (let i = 0; i < newChildren.length; i++) {
      if (i == 0) {
        continue;
      } else {
        let o = newChildren[i];
        const currentBottomLeft = o.y + o.height;
        if (o.y >= el.y && currentBottomLeft <= el.y + el.height) {
          if (currentBottomLeft > lastBottomLeft) {
            lastBottomLeft = currentBottomLeft;
          }
        }
        sectionGroup.push(o);
      }
    }
    if (lastBottomLeft) el.paddingBottom = el.y + el.height - lastBottomLeft;
    sectionGroup.push(updateAttributeSectionByShrink(el));
  } else {
    sectionGroup = newChildren;
  }

  let maxWidth;
  if (attrs.columnCount > 1) {
    maxWidth = `${(1 / attrs.columnCount) * 100}%`;
  }

  const commonStyle = {
    maxWidth,
    minHeight: itemHeight,
    overflow: 'hidden' as const,
    margin: attrs.style.gap !== undefined ? attrs.style.gap / 2 : 8,
  };

  if (sectionGroup.length === 1 && sectionGroup[0].type === 'AutoLayout') {
    // render auto layout here directly
    const listItem = sectionGroup[0];
    return (
      <AutoLayout
        {...listItem}
        style={{
          ...listItem.style,
          ...commonStyle,
        }}
        onPress={(e) => {
          if (!isEmpty(attrs?.actions)) {
            e.preventDefault();
            attrs.onPress &&
              attrs.onPress('', {
                itemId: item._id,
              });
          }
        }}
      />
    );
  }

  return (
    <TouchableOpacity
      onPress={(e) => {
        if (!isEmpty(attrs?.actions)) {
          e.preventDefault();
          attrs.onPress &&
            attrs.onPress('', {
              itemId: item._id,
            });
        }
      }}
      style={commonStyle}
    >
      <ObjectRender
        keyItem={index}
        arrComp={sectionGroup}
        isScreen={false}
        layout={{
          offsetTop: attrs.style.top || attrs?.y,
          offsetLeft: attrs.style.left || attrs?.x,
          parentWidth: attrs?.width,
        }}
      />
    </TouchableOpacity>
  );
};

export default Item;
